import app from 'flarum/forum/app';
app.initializers.add('val-ui-desktop-tags-filter', () => {
  const STYLE_ID = 'val-desktop-tags-filter-style';
  const ITEM_CLASS = 'val-desktop-forum-filter-item';
  const RESULTS_CLASS = 'val-desktop-forum-filter-results';
  const media = window.matchMedia('(min-width: 768px)');
  let observer = null;
  let timer = null;
  let hierarchyPromise = null;
  let hierarchyLoaded = false;
  let searchRun = 0;
  let currentValue = '';
  const childrenLoaded = new Set();
  const childrenLoading = new Map();
  function normalizedPath() {
    const path = window.location.pathname || '/';
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }
  function isDesktopForumSidebarPage() {
    const path = normalizedPath();
    return media.matches && (path === '/all' || path.startsWith('/t/') || path === '/private' || path.startsWith('/private/'));
  }
  function normalizeText(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');
  }
  function tagName(tag) {
    return tag && typeof tag.name === 'function' ? String(tag.name() || '') : '';
  }
  function tagSlug(tag) {
    return tag && typeof tag.slug === 'function' ? String(tag.slug() || '') : '';
  }
  function tagPosition(tag) {
    if (!tag || typeof tag.position !== 'function') return null;
    try {
      return tag.position();
    } catch (_) {
      return null;
    }
  }
  function tagParent(tag) {
    if (!tag || typeof tag.parent !== 'function') return null;
    try {
      return tag.parent();
    } catch (_) {
      return null;
    }
  }
  function tagId(tag) {
    return tag && typeof tag.id === 'function' ? String(tag.id()) : '';
  }
  function childTags(tag) {
    if (!tag) return [];
    let children = [];
    try {
      const related = tag.children && tag.children();
      if (Array.isArray(related)) {
        children = related.filter(Boolean);
      }
    } catch (_) {}
    if (!children.length && app.store && typeof app.store.all === 'function') {
      const id = tagId(tag);
      children = app.store.all('tags').filter(candidate => {
        if (!candidate || candidate === tag || typeof candidate.parent !== 'function') return false;
        try {
          const parent = candidate.parent();
          return parent && tagId(parent) === id;
        } catch (_) {
          return false;
        }
      });
    }
    return children.sort(compareTags);
  }
  function loadChildren(tag) {
    const id = tagId(tag);
    if (!id || !app.store || typeof app.store.find !== 'function') {
      return Promise.resolve([]);
    }
    if (childrenLoaded.has(id)) {
      return Promise.resolve(childTags(tag));
    }
    if (childrenLoading.has(id)) {
      return childrenLoading.get(id);
    }
    const request = app.store.find('tags', id, {
      include: 'children'
    }).catch(() => null).then(() => {
      childrenLoaded.add(id);
      childrenLoading.delete(id);
      return childTags(tag);
    });
    childrenLoading.set(id, request);
    return request;
  }
  function ensureForumHierarchyLoaded() {
    if (hierarchyLoaded) return Promise.resolve();
    if (hierarchyPromise) return hierarchyPromise;

    /*
     * Sur /all, Flarum ne place pas nécessairement tous les sous-forums
     * dans le store. On recharge donc une seule fois chaque forum racine
     * avec sa relation children, exactement comme le fait déjà VAL sur
     * les pages de forums parents.
     */
    const roots = forumTags().filter(tag => !tagParent(tag));
    hierarchyPromise = Promise.all(roots.map(loadChildren)).then(() => {
      hierarchyLoaded = true;
    }).catch(() => {}).then(() => {
      hierarchyPromise = null;
    });
    return hierarchyPromise;
  }
  function compareTags(a, b) {
    const pa = tagPosition(a);
    const pb = tagPosition(b);
    if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    if (pa !== null && pb === null) return -1;
    if (pa === null && pb !== null) return 1;
    return tagName(a).localeCompare(tagName(b), 'fr', {
      sensitivity: 'base'
    });
  }
  function forumTags() {
    if (!app.store || typeof app.store.all !== 'function') return [];
    return app.store.all('tags').filter(tag => tagPosition(tag) !== null).sort(compareTags);
  }
  function tagUrl(tag) {
    const slug = tagSlug(tag);
    if (!slug) return '#';
    return '/t/' + encodeURIComponent(slug);
  }
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      @media (min-width: 768px) {
        .IndexPage-nav .${ITEM_CLASS},
        .sideNav .${ITEM_CLASS} {
          margin: 8px 0 10px;
        }

        .IndexPage-nav .val-desktop-forum-filter-input,
        .sideNav .val-desktop-forum-filter-input {
          width: 100%;
          min-height: 36px;
          box-sizing: border-box;
          padding: 7px 10px;
          border: 1px solid color-mix(in srgb, var(--muted-color) 34%, transparent);
          border-radius: 6px;
          background: var(--body-bg);
          color: var(--text-color);
          box-shadow: none;
          font: inherit;
          font-size: 13px;
          line-height: 20px;
        }

        .IndexPage-nav .val-desktop-forum-filter-input:focus,
        .sideNav .val-desktop-forum-filter-input:focus {
          outline: none;
          border-color: color-mix(in srgb, var(--muted-color) 70%, transparent);
          box-shadow: 0 0 0 1px color-mix(in srgb, var(--muted-color) 12%, transparent);
        }

        .IndexPage-nav .val-desktop-forum-filter-input::placeholder,
        .sideNav .val-desktop-forum-filter-input::placeholder {
          color: var(--muted-color);
          opacity: .9;
        }

        .IndexPage-nav .${RESULTS_CLASS},
        .sideNav .${RESULTS_CLASS} {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .IndexPage-nav .val-desktop-forum-filter-result,
        .sideNav .val-desktop-forum-filter-result {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .IndexPage-nav .val-desktop-forum-filter-result-link,
        .sideNav .val-desktop-forum-filter-result-link {
          display: flex;
          min-height: 32px;
          align-items: center;
          box-sizing: border-box;
          padding: 5px 4px;
          color: var(--muted-color);
          font-size: 13px;
          line-height: 1.25;
          text-decoration: none;
        }

        .IndexPage-nav .val-desktop-forum-filter-result-link:hover,
        .IndexPage-nav .val-desktop-forum-filter-result-link:focus,
        .sideNav .val-desktop-forum-filter-result-link:hover,
        .sideNav .val-desktop-forum-filter-result-link:focus {
          color: var(--link-color);
          text-decoration: none;
        }

        .IndexPage-nav .val-desktop-forum-filter-result-dot,
        .sideNav .val-desktop-forum-filter-result-dot {
          display: block;
          flex: 0 0 auto;
          width: 16px;
          height: 7px;
          margin: 0 12px 0 0;
          border-radius: 999px;
          background: var(--muted-color);
          opacity: .45;
        }

        .IndexPage-nav .val-desktop-forum-filter-result-icon,
        .sideNav .val-desktop-forum-filter-result-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 16px;
          width: 16px;
          margin: 0 12px 0 0;
          font-size: 14px;
          line-height: 1;
        }

        .IndexPage-nav .val-desktop-forum-filter-result--child .val-desktop-forum-filter-result-link,
        .sideNav .val-desktop-forum-filter-result--child .val-desktop-forum-filter-result-link {
          padding-left: 18px;
        }

        .IndexPage-nav .val-desktop-forum-filter-result--depth-2 .val-desktop-forum-filter-result-link,
        .sideNav .val-desktop-forum-filter-result--depth-2 .val-desktop-forum-filter-result-link {
          padding-left: 32px;
        }

        .IndexPage-nav .val-desktop-forum-filter-result--depth-3 .val-desktop-forum-filter-result-link,
        .sideNav .val-desktop-forum-filter-result--depth-3 .val-desktop-forum-filter-result-link {
          padding-left: 46px;
        }

        .IndexPage-nav .val-desktop-forum-filter-context > .val-desktop-forum-filter-result-link,
        .sideNav .val-desktop-forum-filter-context > .val-desktop-forum-filter-result-link {
          font-weight: 600;
          opacity: .78;
        }

        .IndexPage-nav .val-desktop-forum-filter-empty,
        .sideNav .val-desktop-forum-filter-empty {
          padding: 7px 4px 4px;
          color: var(--muted-color);
          font-size: 12px;
          font-style: italic;
        }
      }
    `;
    document.head.appendChild(style);
  }
  function isTagHref(anchor) {
    if (!anchor) return false;
    const href = anchor.getAttribute('href') || '';
    try {
      const url = new URL(href, window.location.origin);
      return /\/t\//.test(url.pathname);
    } catch (_) {
      return /(^|\/)t\//.test(href);
    }
  }
  function firstTagItemIn(container) {
    const anchors = Array.from(container.querySelectorAll('a[href]')).filter(anchor => {
      if (!isTagHref(anchor)) return false;
      if (anchor.closest('.' + ITEM_CLASS)) return false;
      if (anchor.closest('.' + RESULTS_CLASS)) return false;
      return true;
    });
    for (const anchor of anchors) {
      const item = anchor.closest('li');
      if (item && item.parentElement === container) return item;
    }
    return null;
  }
  function nativeForumItems(container, mount) {
    const children = Array.from(container.children);
    const firstTagItem = firstTagItemIn(container);
    if (!firstTagItem) return [];
    const start = children.indexOf(firstTagItem);
    if (start < 0) return [];
    return children.slice(start).filter(item => item !== mount && !item.classList?.contains(RESULTS_CLASS));
  }
  function showNativeForumItems(container, mount) {
    nativeForumItems(container, mount).forEach(item => {
      item.style.removeProperty('display');
    });
  }
  function hideNativeForumItems(container, mount) {
    nativeForumItems(container, mount).forEach(item => {
      item.style.display = 'none';
    });
  }
  function removeResults(container) {
    container.querySelectorAll(':scope > .' + RESULTS_CLASS).forEach(item => item.remove());
  }
  function clearFilter(container, mount) {
    removeResults(container);
    showNativeForumItems(container, mount);
  }
  function removeMounts() {
    document.querySelectorAll('.' + ITEM_CLASS).forEach(mount => {
      const container = mount.parentElement;
      if (container) clearFilter(container, mount);
      mount.remove();
    });
  }
  function buildTree(tags) {
    const byId = new Map(tags.map(tag => [String(tag.id()), tag]));
    const children = new Map();
    tags.forEach(tag => {
      const parent = tagParent(tag);
      const parentId = parent ? String(parent.id()) : '';
      if (!parentId || !byId.has(parentId)) return;
      if (!children.has(parentId)) children.set(parentId, []);
      children.get(parentId).push(tag);
    });
    children.forEach(items => items.sort(compareTags));
    const roots = tags.filter(tag => {
      const parent = tagParent(tag);
      return !parent || !byId.has(String(parent.id()));
    }).sort(compareTags);
    return {
      byId,
      children,
      roots
    };
  }
  function matchingTagIds(tags, needle, byId) {
    const matched = new Set();
    const included = new Set();
    tags.forEach(tag => {
      if (normalizeText(tagName(tag)).includes(needle)) {
        matched.add(String(tag.id()));
      }
    });
    matched.forEach(id => {
      let tag = byId.get(id);
      const guard = new Set();
      while (tag) {
        const currentId = String(tag.id());
        if (guard.has(currentId)) break;
        guard.add(currentId);
        included.add(currentId);
        const parent = tagParent(tag);
        if (!parent) break;
        tag = byId.get(String(parent.id())) || null;
      }
    });
    return {
      matched,
      included
    };
  }
  function makeResultItem(tag, depth, isContext) {
    const item = document.createElement('li');
    item.className = 'val-desktop-forum-filter-result ' + (depth > 0 ? 'val-desktop-forum-filter-result--child ' : '') + 'val-desktop-forum-filter-result--depth-' + Math.min(depth, 3) + (isContext ? ' val-desktop-forum-filter-context' : '');
    const link = document.createElement('a');
    link.className = 'val-desktop-forum-filter-result-link';
    link.href = tagUrl(tag);
    const parent = tagParent(tag);
    const ownColor = tag && typeof tag.color === 'function' ? tag.color() : null;
    const parentColor = parent && typeof parent.color === 'function' ? parent.color() : null;
    const markerColor = ownColor || parentColor;
    const ownIcon = tag && typeof tag.icon === 'function' ? tag.icon() : null;
    const parentIcon = parent && typeof parent.icon === 'function' ? parent.icon() : null;
    const markerIcon = ownIcon || parentIcon;
    let marker;
    if (markerIcon) {
      marker = document.createElement('i');
      marker.className = 'icon ' + markerIcon + ' val-desktop-forum-filter-result-icon';
      marker.setAttribute('aria-hidden', 'true');
      if (markerColor) {
        marker.style.setProperty('color', String(markerColor), 'important');
      }
    } else {
      marker = document.createElement('span');
      marker.className = 'val-desktop-forum-filter-result-dot';
      marker.setAttribute('aria-hidden', 'true');
      if (markerColor) {
        marker.style.setProperty('background-color', String(markerColor), 'important');
        marker.style.setProperty('border-color', String(markerColor), 'important');
      }
    }
    const label = document.createElement('span');
    label.textContent = tagName(tag);
    link.appendChild(marker);
    link.appendChild(label);
    item.appendChild(link);
    return item;
  }
  function renderIncludedBranch(fragment, tag, depth, tree, matched, included) {
    const id = String(tag.id());
    if (!included.has(id)) return;
    fragment.appendChild(makeResultItem(tag, depth, !matched.has(id)));
    const children = tree.children.get(id) || [];
    children.forEach(child => {
      renderIncludedBranch(fragment, child, depth + 1, tree, matched, included);
    });
  }
  function renderStatus(container, mount, text) {
    removeResults(container);
    const results = document.createElement('li');
    results.className = RESULTS_CLASS;
    const status = document.createElement('div');
    status.className = 'val-desktop-forum-filter-empty';
    status.textContent = text;
    results.appendChild(status);
    const anchor = mount.nextElementSibling;
    container.insertBefore(results, anchor || null);
  }
  async function applyFilter(container, mount, value) {
    currentValue = value;
    const needle = normalizeText(value.trim());
    const run = ++searchRun;
    if (!needle) {
      clearFilter(container, mount);
      return;
    }
    hideNativeForumItems(container, mount);
    renderStatus(container, mount, 'Recherche…');
    await ensureForumHierarchyLoaded();
    if (run !== searchRun) return;
    removeResults(container);
    const tags = forumTags();
    const tree = buildTree(tags);
    const {
      matched,
      included
    } = matchingTagIds(tags, needle, tree.byId);
    const results = document.createElement('li');
    results.className = RESULTS_CLASS;
    if (!matched.size) {
      const empty = document.createElement('div');
      empty.className = 'val-desktop-forum-filter-empty';
      empty.textContent = 'Aucun forum trouvé';
      results.appendChild(empty);
    } else {
      const fragment = document.createDocumentFragment();
      tree.roots.forEach(root => {
        renderIncludedBranch(fragment, root, 0, tree, matched, included);
      });
      results.appendChild(fragment);
    }
    const anchor = mount.nextElementSibling;
    container.insertBefore(results, anchor || null);
  }
  function findSidebarAndFirstTag() {
    const sidebars = Array.from(document.querySelectorAll('.TagsPage .IndexPage-nav.sideNav, .TagsPage .IndexPage-nav, .TagsPage .sideNav, .IndexPage-nav.sideNav, .IndexPage-nav'));
    for (const sidebar of sidebars) {
      // Ne jamais considérer nos propres résultats de recherche comme
      // la liste native de la sidebar. Sinon le MutationObserver remonte
      // un second champ à l'intérieur des résultats dès la première frappe.
      const tagAnchors = Array.from(sidebar.querySelectorAll('a[href]')).filter(anchor => {
        if (!isTagHref(anchor)) return false;
        if (anchor.closest('.' + ITEM_CLASS)) return false;
        if (anchor.closest('.' + RESULTS_CLASS)) return false;
        return true;
      });
      if (!tagAnchors.length) continue;
      const firstTagItem = tagAnchors.map(anchor => anchor.closest('li')).find(item => item && item.parentElement);
      const container = firstTagItem?.parentElement;
      if (firstTagItem && container) {
        return {
          firstTagItem,
          container
        };
      }
    }
    return null;
  }
  function hideSecondaryTagsInSidebar() {
    document.querySelectorAll('.IndexPage-nav li[class*="item-tag"], .sideNav li[class*="item-tag"]').forEach(item => {
      const idClass = Array.from(item.classList).find(name => /^item-tag\d+$/.test(name));
      if (!idClass) return;
      const id = idClass.replace('item-tag', '');
      const tag = app.store.getById('tags', id);
      if (!tag) return;

      /*
       * Flarum : les tags secondaires n'ont pas de position.
       * On ne les affiche pas dans la sidebar Desktop.
       */
      if (tagPosition(tag) === null) {
        item.style.display = 'none';
      }
    });
  }
  function inheritParentIconsInSidebar() {
    document.querySelectorAll('.IndexPage-nav .TagLinkButton.child').forEach(link => {
      const item = link.closest('li[class*="item-tag"]');
      if (!item) return;
      const idClass = Array.from(item.classList).find(name => /^item-tag\d+$/.test(name));
      if (!idClass) return;
      const id = idClass.replace('item-tag', '');
      const tag = app.store.getById('tags', id);
      if (!tag) return;
      const ownIcon = typeof tag.icon === 'function' ? tag.icon() : null;
      if (ownIcon) return;
      const parent = tagParent(tag);
      if (!parent) return;
      const parentIcon = typeof parent.icon === 'function' ? parent.icon() : null;
      const parentColor = typeof parent.color === 'function' ? parent.color() : null;
      if (!parentIcon) return;
      const icon = link.querySelector('.Button-icon');
      if (!icon) return;
      icon.className = 'Button-icon text-colored icon ' + parentIcon + ' val-inherited-parent-icon';
      if (parentColor) {
        icon.style.setProperty('--color', parentColor);
        icon.style.setProperty('color', parentColor);
      }
    });
  }
  function mountFilter() {
    /*
     * Le masquage des tags secondaires vaut pour toute sidebar Desktop,
     * y compris Byobu / messages privés.
     *
     * Le filtre de recherche reste, lui, limité à /all et /t/...
     */
    if (media.matches) {
      hideSecondaryTagsInSidebar();
    }
    if (!isDesktopForumSidebarPage()) {
      removeMounts();
      return;
    }
    ensureStyle();
    const found = findSidebarAndFirstTag();
    if (!found) return;
    const {
      firstTagItem,
      container
    } = found;
    inheritParentIconsInSidebar();
    let mount = container.querySelector(':scope > .' + ITEM_CLASS);
    if (!mount) {
      mount = document.createElement('li');
      mount.className = ITEM_CLASS;
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'FormControl val-desktop-forum-filter-input';
      input.placeholder = 'Rechercher un forum…';
      input.setAttribute('aria-label', 'Rechercher un forum');
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.value = currentValue;
      input.addEventListener('input', () => {
        applyFilter(container, mount, input.value);
      });
      mount.appendChild(input);
      container.insertBefore(mount, firstTagItem);
      if (currentValue) {
        applyFilter(container, mount, currentValue);
      }
    } else if (mount.nextElementSibling !== firstTagItem && !mount.nextElementSibling?.classList?.contains(RESULTS_CLASS)) {
      container.insertBefore(mount, firstTagItem);
    }
  }
  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(mountFilter, 25);
  }
  observer = new MutationObserver(schedule);
  const start = () => {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    schedule();
  };
  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  }
  window.addEventListener('popstate', schedule);
  window.addEventListener('hashchange', schedule);
  media.addEventListener?.('change', schedule);
});